/**
 * Rate Limit 에러 처리 유틸리티
 * - 통일된 에러 메시지 제공
 * - 공지사항 링크 포함
 */

// Rate Limit 관련 키워드
const RATE_LIMIT_KEYWORDS = [
  '429',
  'rate limit',
  'rate_limit',
  'resource exhausted',
  '요청 제한',
  '요청이 많아',
  '모든 API 모델'
];

/**
 * Rate Limit 에러인지 확인
 * @param {Error|string} error - 에러 객체 또는 메시지
 * @returns {boolean}
 */
export const isRateLimitError = (error) => {
  const message = typeof error === 'string' ? error : (error?.message || '');
  const lowerMessage = message.toLowerCase();
  
  return RATE_LIMIT_KEYWORDS.some(keyword => 
    lowerMessage.includes(keyword.toLowerCase())
  );
};

/**
 * Rate Limit 에러 메시지 생성
 * @param {Object} options - 옵션
 * @param {boolean} options.includeLink - 공지사항 링크 포함 여부
 * @param {boolean} options.html - HTML 형식 여부
 * @returns {string}
 */
export const getRateLimitMessage = (options = {}) => {
  const { includeLink = true, html = false } = options;
  
  const baseMessage = '지금 많은 분들이 이용 중이에요.';
  const details = [
    '다른 AI가 대신 처리하고 있어요',
    '결과물이 평소와 다소 다를 수 있어요',
    '잠시 후 다시 시도해 주세요'
  ];
  
  if (html) {
    let htmlMessage = `<strong>${baseMessage}</strong><ul>`;
    details.forEach(detail => {
      htmlMessage += `<li>${detail}</li>`;
    });
    if (includeLink) {
      htmlMessage += `<li><a href="/help/notice/file-attachment-limit-2025-12" style="color: #1a73e8;">자세한 안내 보기</a></li>`;
    }
    htmlMessage += '</ul>';
    return htmlMessage;
  }
  
  let textMessage = baseMessage + '\n';
  details.forEach(detail => {
    textMessage += `• ${detail}\n`;
  });
  if (includeLink) {
    textMessage += '\n자세한 안내는 아래 링크에서 확인해 주세요.\n→ 고객센터 > 공지사항';
  }
  
  return textMessage;
};

/**
 * Rate Limit 에러 처리 (Toast 표시용 - 에러 스타일, 사라지지 않음)
 * @param {Function} showToast - Toast 표시 함수
 * @param {Object} options - 추가 옵션
 */
export const handleRateLimitError = (showToast, options = {}) => {
  const message = getRateLimitMessage({ includeLink: false, html: false });
  
  if (showToast) {
    // 에러 스타일로, duration=0이면 안 사라짐
    showToast(message, 'error', 0);
  }
  
  return message;
};

/**
 * Rate Limit 에러 표시 (에러 토스트 - 사라지지 않음)
 * @param {Function} showToast - Toast 표시 함수 (없으면 console.error로 대체)
 * @returns {string} 에러 메시지
 */
export const showRateLimitAlert = (showToast) => {
  const message = getRateLimitMessage({ includeLink: true, html: false });
  
  if (showToast) {
    // 에러 스타일, duration=0이면 안 사라짐 (수동으로 닫아야 함)
    showToast(message, 'error', 0);
  } else {
    // showToast가 없으면 콘솔에만 출력
    console.error('[Rate Limit]', message);
  }
  
  return message;
};

/**
 * 에러 메시지 처리 (Rate Limit 감지 시 통일된 메시지 반환)
 * @param {Error|string} error - 에러 객체 또는 메시지
 * @param {Object} options - 옵션
 * @returns {string} 처리된 에러 메시지
 */
export const processErrorMessage = (error, options = {}) => {
  if (isRateLimitError(error)) {
    return getRateLimitMessage(options);
  }
  
  // Rate Limit이 아닌 경우 원본 메시지 반환
  return typeof error === 'string' ? error : (error?.message || '알 수 없는 오류가 발생했습니다.');
};

/**
 * Rate Limit 에러용 React 컴포넌트 내용
 * (JSX에서 사용)
 */
export const RateLimitErrorContent = {
  title: '지금 많은 분들이 이용 중이에요.',
  points: [
    '다른 AI가 대신 처리하고 있어요',
    '결과물이 평소와 다소 다를 수 있어요',
    '잠시 후 다시 시도해 주세요'
  ],
  linkText: '자세한 안내 보기',
  linkUrl: '/help/notice/file-attachment-limit-2025-12'
};

